import "@/App.css";
import React, { useEffect, useState, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { HeroSection } from "@/components/HeroSection";
import { AboutSection } from "@/components/AboutSection";
import { ExpertiseSection } from "@/components/ExpertiseSection";
import { GallerySection } from "@/components/GallerySection";
import { ExperienceSection } from "@/components/ExperienceSection";
import { ProofSection } from "@/components/ProofSection";
import { ContactSection } from "@/components/ContactSection";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import BookCall from "@/pages/BookCall";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Theme Context
const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

// Theme Provider
const ThemeProvider = ({ children }) => {
  const [themeMode, setThemeMode] = useState('dark'); // 'dark', 'light', 'optional'
  const [currentTheme, setCurrentTheme] = useState('dark'); // actual applied theme
  const [isLoading, setIsLoading] = useState(true);

  // Fetch theme settings from backend
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/settings`);
        if (response.ok) {
          const data = await response.json();
          setThemeMode(data.theme || 'dark');
          
          if (data.theme === 'optional') {
            // Check localStorage for user preference
            const savedTheme = localStorage.getItem('user-theme') || 'dark';
            setCurrentTheme(savedTheme);
          } else {
            setCurrentTheme(data.theme || 'dark');
          }
        }
      } catch (error) {
        console.error('Error fetching theme settings:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Apply theme to document
  useEffect(() => {
    if (currentTheme === 'light') {
      document.documentElement.classList.add('light');
    } else {
      document.documentElement.classList.remove('light');
    }
  }, [currentTheme]);

  // Toggle theme (for optional mode)
  const toggleTheme = () => {
    if (themeMode === 'optional') {
      const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
      setCurrentTheme(newTheme);
      localStorage.setItem('user-theme', newTheme);
    }
  };

  return (
    <ThemeContext.Provider value={{ 
      themeMode, 
      currentTheme, 
      toggleTheme,
      isLoading,
      canToggle: themeMode === 'optional'
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

// DevTools Protection Hook
const useDevToolsProtection = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Skip protection for admin pages
    if (location.pathname.startsWith('/ma') || location.pathname.startsWith('/admin')) {
      return;
    }

    let isProtectionEnabled = false;

    // Fetch settings from backend
    const checkSettings = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/settings`);
        if (response.ok) {
          const data = await response.json();
          isProtectionEnabled = data.dev_tools_disabled;
          
          if (isProtectionEnabled) {
            enableProtection();
          }
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };

    const enableProtection = () => {
      document.addEventListener('contextmenu', preventAction);
      document.addEventListener('keydown', preventKeyboard);
    };

    const preventAction = (e) => {
      if (isProtectionEnabled) {
        e.preventDefault();
        return false;
      }
    };

    const preventKeyboard = (e) => {
      if (!isProtectionEnabled) return;
      
      if (e.ctrlKey && e.key === 'u') { e.preventDefault(); return false; }
      if (e.ctrlKey && e.shiftKey && e.key === 'I') { e.preventDefault(); return false; }
      if (e.ctrlKey && e.shiftKey && e.key === 'J') { e.preventDefault(); return false; }
      if (e.ctrlKey && e.shiftKey && e.key === 'C') { e.preventDefault(); return false; }
      if (e.key === 'F12') { e.preventDefault(); return false; }
      if (e.ctrlKey && e.key === 's') { e.preventDefault(); return false; }
    };

    checkSettings();

    return () => {
      document.removeEventListener('contextmenu', preventAction);
      document.removeEventListener('keydown', preventKeyboard);
    };
  }, [location.pathname]);
};

// Main Portfolio Page
const PortfolioPage = () => {
  return (
    <div className="min-h-screen bg-background relative overflow-x-hidden transition-colors duration-300">
      <Navigation />
      <main>
        <HeroSection />
        <AboutSection />
        <ExpertiseSection />
        <GallerySection />
        <ExperienceSection />
        <ProofSection />
        <ContactSection />
      </main>
    </div>
  );
};

// App Content with Protection
const AppContent = () => {
  useDevToolsProtection();
  
  return (
    <Routes>
      <Route path="/" element={<PortfolioPage />} />
      <Route path="/ma" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
      <Route path="/book-call" element={<BookCall />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
